import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts'
import { formatNumber } from '~/utils/helpers'
import { CowResultsType, CattleResultsType } from '~/api/services/climaCheck'
import { Nullable } from '~/types'
import DairyCowImage from '@assets/svgs/klima/Icon_Milchkuh.svg'
import HeiferImage from '@assets/svgs/klima/Icon_Farse.svg'
import InfoPopup from '~/components/InfoPopup'
import { EmissionWrapper, TabEl } from '~/pages/Dairy/ClimateCheck/ResultsClimateCheck/ResultsModal/style'
import { EmissionCard } from './EmissionCard'

const COLORS = ['#4A90E2', '#FF9800', '#4CAF50', '#FFC107', '#9C27B0', '#F44336', '#795548', '#607D8B'];

// Create a stable color mapping for each key
const COLOR_MAP: Record<string, string> = {
  // Cow keys
  inventoryAddition: COLORS[0],
  calfRearing: COLORS[1],
  digestion: COLORS[2],
  feedProductionAndLosses: COLORS[3],
  manureAndBedding: COLORS[4],
  waterAndEnergy: COLORS[5],
  dieselProductionAndCombustion: COLORS[6],
  machineManufacturing: COLORS[7],
  // Heifer keys
  youngCattleFemaleCalf: COLORS[0],
  youngCattleDigestion: COLORS[1],
  youngCattleFeedProductionAndLosses: COLORS[2],
  youngCattleManureAndBedding: COLORS[3],
  youngCattleWaterAndEnergy: COLORS[4],
  youngCattleDieselProductionAndCombustion: COLORS[5],
  youngCattleMachineManufacturing: COLORS[6]
};

type TabKey = 'cows' | 'youngCattle';

const cowColumnHeaders = [
  {
    name: 'THG-Emissionen gesamt',
    key: 'total',
    unit: 'kg CO₂-Äq./Kuh u. Jahr',
    total: true,
    pieValue: false
  },
  {
    name: 'THG-Emissionen je kg Milch (FPCM)',
    key: 'ghgEmissionMilkIdf',
    unit: 'kg CO₂-Äq./kg Milch',
    pieValue: false,
    decimal: 2
  },
  {
    name: 'THG-Emissionen Schlachtkuh',
    key: 'ghgEmissionSlaughterCowsIdf',
    unit: 'kg CO₂-Äq./kg LG',
    pieValue: false,
    decimal: 2
  },
  {
    name: 'THG-Emissionen Kalb',
    key: 'ghgEmissionCalvesIdf',
    unit: 'kg CO₂-Äq./kg LG',
    pieValue: false,
    decimal: 2
  },
  {
    name: 'Bestandsergänzung',
    key: 'inventoryAddition',
    unit: '%',
    pieValue: true
  },
  {
    name: 'Kälberaufzucht',
    key: 'calfRearing',
    unit: '%',
    pieValue: true
  },
  {
    name: 'Verdauung',
    key: 'digestion',
    unit: '%',
    pieValue: true
  },
  {
    name: 'Futtermittelherstellung und -verluste',
    key: 'feedProductionAndLosses',
    unit: '%',
    pieValue: true
  },
  {
    name: 'Wirtschaftsdünger und Einstreu',
    key: 'manureAndBedding',
    unit: '%',
    pieValue: true
  },
  {
    name: 'Wasser- und Energieeinsatz',
    key: 'waterAndEnergy',
    unit: '%',
    pieValue: true
  },
  {
    name: 'Dieselherstellung und -verbrennung',
    key: 'dieselProductionAndCombustion',
    unit: '%',
    pieValue: true
  },
  {
    name: 'Maschinenherstellung',
    key: 'machineManufacturing',
    unit: '%',
    pieValue: true
  }
];

const heiferColumnHeaders = [
  {
    name: 'THG-Emissionen gesamt Färsenaufzucht',
    key: 'youngCattleTotal',
    unit: 'kg CO₂-Äq./erz. Färse',
    total: true,
    pieValue: false
  },
  {
    name: 'THG-Emissionen je erz. Färse (IDF)',
    key: 'youngCattleGhgEmissionHeifersIdf',
    unit: 'kg CO₂-Äq./erz. Färse',
    pieValue: false,
    decimal: 2
  },
  {
    name: 'weibliches Kalb',
    key: 'youngCattleFemaleCalf',
    unit: '%',
    pieValue: true
  },
  {
    name: 'Verdauung',
    key: 'youngCattleDigestion',
    unit: '%',
    pieValue: true
  },
  {
    name: 'Futtermittelherstellung und -verluste',
    key: 'youngCattleFeedProductionAndLosses',
    unit: '%',
    pieValue: true
  },
  {
    name: 'Wirtschaftsdünger und Einstreu',
    key: 'youngCattleManureAndBedding',
    unit: '%',
    pieValue: true
  },
  {
    name: 'Wasser- und Energieeinsatz',
    key: 'youngCattleWaterAndEnergy',
    unit: '%',
    pieValue: true
  },
  {
    name: 'Dieselherstellung und -verbrennung',
    key: 'youngCattleDieselProductionAndCombustion',
    unit: '%',
    pieValue: true
  },
  {
    name: 'Maschinenherstellung',
    key: 'youngCattleMachineManufacturing',
    unit: '%',
    pieValue: true
  }
];

interface Props {
  results: {
    cowResults: CowResultsType
    heiferResults: Nullable<CattleResultsType>
  }
  removeMargin?: boolean
}

const ResultsCo2: React.FC<Props> = ({ results }) => {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<TabKey>('cows');

  const getColumnHeaders = () => {
    switch (activeTab) {
      case 'cows':
        return cowColumnHeaders;
      case 'youngCattle':
        return heiferColumnHeaders;
      default:
        return cowColumnHeaders;
    }
  };

  const getActiveResults = () => {
    switch (activeTab) {
      case 'cows':
        return results.cowResults;
      case 'youngCattle':
        return results.heiferResults;
      default:
        return results.cowResults;
    }
  };

  const getTotalSumKey = () => {
    switch (activeTab) {
      case 'cows':
        return 'total';
      case 'youngCattle':
        return 'youngCattleTotal';
      default:
        return 'total';
    }
  };

  const getPieChartData = () => {
    const data = getActiveResults();
    const totalKey = getTotalSumKey()
    const total = data[totalKey]
    if (!data) return [];

    const headers = getColumnHeaders().filter(header => header.pieValue);
    const pieData = [];
    headers.forEach(header => {
      const value = data[header.key];
      if (parseFloat(value) > 0) {
        pieData.push({
          name: header.name,
          value: (parseFloat(value) / total) * 100,
          key: header.key,
          color: COLOR_MAP[header.key] || '#000'
        });
      }
    });
    return pieData.sort((a, b) => b.value - a.value);
  };

  if (!results.cowResults) {
    return <></>;
  }

  const getTotalKey = () => {
    switch (activeTab) {
      case 'cows':
        return 'total';
      case 'youngCattle':
        return 'youngCattleTotal';
    }
  };

  const getResultsTitle = () => {
    switch (activeTab) {
      case 'cows':
        return 'THG-Emissionen (in kg CO<sub>2</sub>-Äq./Kuh u. Jahr)';
      case 'youngCattle':
        return 'Verteilung der THG-Emissionen (kg CO<sub>2</sub>-Äq./je erz. Färse)';
    }
  };

  const hasYoungCattle = results.heiferResults && Object.keys(results.heiferResults).length > 0;

  const totalSegments = hasYoungCattle ? 2 : 1;

  const renderLegend = () => {
    const activeResults = getActiveResults();
    const totalKey = getTotalSumKey()
    const total = activeResults[totalKey]
    if (!activeResults) return null;
    return getColumnHeaders()
      .filter((header) => header.pieValue && !header.total)
      .map((header) => {
        const value = activeResults[header.key];
        return {
          header,
          value: (parseFloat(value) / total) * 100,
          color: COLOR_MAP[header.key] || '#000'
        };
      })
      .sort((a, b) => b.value - a.value)
      .map(({ header, value, color }) => {
        const headerWithInfo = header as any;
        return (
          <TabEl.SmallCard key={header.key}>
            <TabEl.SmallCardLabel>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    backgroundColor: color,
                    borderRadius: '2px'
                  }}
                />
                {header.name}
              </div>
              {headerWithInfo.info && (
                <InfoPopup
                  content={headerWithInfo.info}
                  position='right center'
                  styleIcon={{ fontSize: '1.3em' }}
                  miWidth={400}
                />
              )}
            </TabEl.SmallCardLabel>
            <TabEl.SmallCardValue>
              {`${formatNumber(value || 0, 2)}%`}
            </TabEl.SmallCardValue>
          </TabEl.SmallCard>
        );
      });
  };

  return (
    <div>
      <EmissionWrapper $resultSegments={totalSegments <= 1 ? 2 : totalSegments}>
        <EmissionCard
          title={t('GHG emissions per cow per year')}
          value={results.cowResults.total}
          unit='kg CO<sub>2</sub>-Äq./Kuh u. Jahr'
          description='Dieser Wert entspricht der Summe aller berücksichtigten Treibhausgasemissionen.'
          icon={<img src={DairyCowImage} style={{ width: 28, borderRadius: '50%' }} />}
          variant='cows'
          isActive={activeTab === 'cows'}
          onClick={() => setActiveTab('cows')}
          additionalDetail={{
            value: results.cowResults.ghgEmissionMilkIdf,
            unit: 'kg CO<sub>2</sub>-Äq./kg Milch (FPCM)',
            label: ''
          }}
        />
        {hasYoungCattle && (
          <EmissionCard
            title='THG-Emissionen gesamt Färsenaufzucht'
            value={results.heiferResults.youngCattleTotal}
            unit='kg CO<sub>2</sub>-Äq./erz. Färse'
            description='Gesamtemissionen der Färsenaufzucht einschließlich Fütterung, Haltung, Wasserversorgung und Energieverbrauch nach IDF-Standard vom Kalb bis zur tragenden Färse.'
            icon={<img src={HeiferImage} style={{ width: 28, borderRadius: '50%' }} />}
            variant='youngCattle'
            isActive={activeTab === 'youngCattle'}
            onClick={() => setActiveTab('youngCattle')}
          />
        )}
      </EmissionWrapper>

      <TabEl.ContentWrapper>
        <TabEl.Segment $widthSegment={'650px'}>
          <TabEl.SegmentTitle>{'Ergebnisübersicht der THG-Emissionen'}</TabEl.SegmentTitle>
          {getColumnHeaders()
            .filter((header) => !header.pieValue && !header.total)
            .map((header) => {
              const activeResults = getActiveResults();
              if (!activeResults) return null;
              const value = activeResults[header.key];
              const headerWithInfo = header as any;
              return (
                <TabEl.SmallCard key={header.key}>
                  <TabEl.SmallCardLabel>
                    {header.name}
                    {headerWithInfo.info && (
                      <InfoPopup
                        content={headerWithInfo.info}
                        position='right center'
                        styleIcon={{ fontSize: '1.3em' }}
                        style={{ minWidth: 400 }}
                        miWidth={600}
                      />
                    )}
                  </TabEl.SmallCardLabel>
                  <TabEl.SmallCardValue>
                    {formatNumber(value, header?.decimal || 0)}
                    {header.unit && (
                      <div
                        dangerouslySetInnerHTML={{ __html: header.unit }}
                        className='unit'
                        style={{ fontSize: '12px' }}
                      />
                    )}
                  </TabEl.SmallCardValue>
                </TabEl.SmallCard>
              );
            })}
        </TabEl.Segment>

        <TabEl.Segment>
          <TabEl.SegmentTitle>
            <div dangerouslySetInnerHTML={{ __html: getResultsTitle() }} />
          </TabEl.SegmentTitle>
          <TabEl.ChartContainer>
            <ResponsiveContainer width='100%' height='100%'>
              <PieChart>
                <Pie
                  data={getPieChartData()}
                  cx='50%'
                  cy='50%'
                  labelLine={false}
                  label={false}
                  outerRadius={150}
                  fill='#8884d8'
                  dataKey='value'
                  startAngle={90}
                  endAngle={-270}
                  nameKey='name'
                >
                  {getPieChartData().map((entry) => (
                    <Cell key={`cell-${entry.key}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number, name: string) => [
                    `${formatNumber(value, 2)}%`,
                    name
                  ]}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </TabEl.ChartContainer>
          {renderLegend()}
        </TabEl.Segment>
      </TabEl.ContentWrapper>
    </div>
  );
};

export default ResultsCo2